/**
 * # FindAndApply
 *
 * A component that allows users to search and select items from a list.
 *
 * ## Props extends `ContainerStyleProps`
 *
 * - `children`: React.ReactNode - The content of the component.
 * - `onApply`: (selectedValues: List<GenericModelType>) => void - A callback function that is called when the user applies the selected values.
 * - `onDismiss`: () => void - A callback function that is called when the user dismisses the component.
 * - `defaultSelectedItems`: List<GenericModelType> - The default selected items.
 * - `radio`: boolean - Indicates whether the selection should be radio buttons instead of checkboxes.
 * - `onSearchTermChange`: (string) => void - A callback function that is called when the search term changes.
 * - `onSearchTermClear`: () => void - A callback function that is called when the search term is cleared.
 * - `onError`: (e: any) => void - A callback function that is called when an error occurs.
 * - `query`: QueryBuilder - The query builder for fetching search results.
 * - `fetchInitial`: boolean - Indicates whether to fetch search results initially without waiting for user search input.
 * - `...rest: ContainerStyleProps`: container style options (background, border, roundness, shadow, padding)
 *
 * ## Usage
 *
 * ```tsx
 * <FindAndApply
 *   onApply={(selectedValues) => {
 *     console.log('Selected Values:', selectedValues);
 *   }}
 *   onDismiss={() => {
 *     console.log('Component dismissed');
 *   }}
 *   defaultSelectedItems={defaultSelectedItems}
 *   radio={true}
 *   onSearchTermChange={(searchTerm) => {
 *     console.log('Search Term:', searchTerm);
 *   }}
 *   onSearchTermClear={() => {
 *     console.log('Search term cleared');
 *   }}
 *   onError={(error) => {
 *     console.error('Error:', error);
 *   }}
 *   query={queryBuilder}
 *   fetchInitial={true}
 * >
 *   {/* Content *\/}
 * </FindAndApply>
 * ```
 */
import React, {useContext, useState, useCallback, useEffect, useRef, type JSX} from 'react';

import type {GenericModel} from 'resources/Generic.model';
import {Set, List} from 'immutable';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import type {QueryBuilder} from 'src/albert-io/json-api-framework/request/builder';

import CheckboxChip from '@albert-io/atomic/molecules/CheckboxChip/CheckboxChip.react';
import type {ChipColor} from '@albert-io/atomic/atoms/Chip/Chip.react';

import LoadingIndicator from 'generic/LoadingIndicator.react';

import type {PaddingLevel} from '../../utils/paddingLevelProp';
import type {
  CardBackgroundColor,
  CardBorder,
  CardRoundness,
  CardShadow
} from '../../utils/getCardClassName';
import type {ButtonProps} from '../../atoms/Button/Button.react';
import Button from '../../atoms/Button/Button.react';
import SplitCard from '../../molecules/SplitCard/SplitCard.react';
import type {Props as SearchInputProps} from '../../molecules/SearchInput/SearchInput.react';
import SearchInput from '../../molecules/SearchInput/SearchInput.react';
import Radio from '../../atoms/Radio/Radio.react';
import Checkbox from '../../atoms/Checkbox/Checkbox.react';
import ListGroup from '../../atoms/ListGroup/ListGroup.react';
import ListGroupItem from '../../atoms/ListGroupItem/ListGroupItem.react';
import Label from '../../atoms/Label/Label.react';
import type {TextProps} from '../../atoms/Text/Text.react';
import Text from '../../atoms/Text/Text.react';
import ButtonGroup from '../../molecules/ButtonGroup/ButtonGroup.react';
import type {IconProp} from '../../atoms/Icon/Icon.react';

import './find-and-apply.scss';

type GenericModelType = {getId: () => string} & typeof GenericModel;

type QueryFn = (searchString: string) => QueryBuilder;

interface State {
  searchString: string;
  selectedItems: Set<GenericModelType>;
  setSelectedItems: (items: Set<GenericModelType>) => void;
  onSearchTermChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSearchString: (string) => void;
  onSearchTermClear?: () => void;
  radio: boolean;
  onApply: (values: List<GenericModelType>) => void;
  onClear?: () => void;
  searchResults: List<GenericModelType>;
  query: QueryFn;
  onDismiss?: () => void;
  fetchInitial: boolean;
}
const FindAndApplyContext = React.createContext<State>(undefined!);

const noop = () => {};

interface ContainerStyleProps extends PropsWithChildrenOptional, PropsWithClassNameOptional {
  as?: 'div';
  backgroundColor?: CardBackgroundColor;
  border?: CardBorder;
  roundness?: CardRoundness;
  shadow?: CardShadow;
  paddingLevel?: PaddingLevel | null;
}

interface Props extends OmitChildren<ContainerStyleProps>, PropsWithChildrenRequired {
  onApply: (selectedValues: List<GenericModelType>) => void;
  onClear?: () => void;
  onDismiss?: () => void;
  defaultSelectedItems?: List<GenericModelType>;
  radio?: boolean;
  onSearchTermChange?: (string) => void;
  onSearchTermClear?: () => void;
  onError?: (e: any) => void;
  query: QueryFn;
  fetchInitial?: boolean;
}

const FindAndApply = ({
  children,
  onApply,
  onClear,
  defaultSelectedItems = List(),
  radio = false,
  onSearchTermChange,
  onSearchTermClear,
  fetchInitial = false,
  onDismiss,
  onError = noop,
  query,
  ...rest
}: Props) => {
  const [searchString, setSearchString] = useState('');
  const [selectedItems, setSelectedItems] = useState(Set(defaultSelectedItems));
  const [searchResults, setSearchResults] = useState<List<GenericModelType>>(List());

  const mergeResults = useCallback(
    (results: unknown) => {
      // getResourcePromise resolves to unknown; at runtime this is a List of models
      const newResults = results as List<GenericModelType>;
      const mergedResults = newResults.map((newResult) => {
        const selectedResult = selectedItems.find(
          (selectedItem) => selectedItem?.getId() === newResult?.getId()
        );
        return selectedResult || newResult;
      }) as List<GenericModelType>;
      setSearchResults(mergedResults);
    },
    [selectedItems]
  );

  const firstUpdate = useRef(true);

  // Reset the (locally overridable) selection when the caller swaps in a new default list,
  // adjusted during render instead of in an effect. Keyed on reference identity, same as the
  // former effect's dependency.
  const [prevDefaultSelectedItems, setPrevDefaultSelectedItems] = useState(defaultSelectedItems);
  if (prevDefaultSelectedItems !== defaultSelectedItems) {
    setPrevDefaultSelectedItems(defaultSelectedItems);
    setSelectedItems(Set(defaultSelectedItems));
  }

  // The search-term callbacks belong to the event that changes the term, not to the fetch
  // effect below: driven by the effect they also fired on mount (reporting a "clear" nobody
  // asked for) and again on every selection change, because the effect re-runs whenever
  // `mergeResults` is rebuilt from `selectedItems`.
  const handleSearchStringChange = useCallback(
    (value: string) => {
      setSearchString(value);
      if (value.length) {
        onSearchTermChange?.(value);
      } else {
        onSearchTermClear?.();
      }
    },
    [onSearchTermChange, onSearchTermClear]
  );

  useEffect(() => {
    if (fetchInitial && firstUpdate.current) {
      firstUpdate.current = false;
      query(searchString).getResourcePromise().then(mergeResults).catch(onError);
    } else if (searchString.length) {
      // make actual query and merge results with default items
      query(searchString).getResourcePromise().then(mergeResults).catch(onError);
    } else if (fetchInitial) {
      query(searchString).getResourcePromise().then(mergeResults).catch(onError);
    }
  }, [searchString, mergeResults, query, fetchInitial, onError]);

  return (
    <FindAndApplyContext.Provider
      value={{
        searchString,
        setSearchString: handleSearchStringChange,
        selectedItems,
        onSearchTermChange,
        onSearchTermClear,
        radio,
        onApply,
        onClear,
        setSelectedItems,
        searchResults,
        fetchInitial,
        onDismiss,
        query
      }}
    >
      <SplitCard {...rest}>{children}</SplitCard>
    </FindAndApplyContext.Provider>
  );
};

type HeaderProps = OmitChildren<ContainerStyleProps> & {
  children?: (
    state: State & {
      HeaderSearchInput: (props: Omit<SearchInputProps, 'onChange' | 'onReset'>) => JSX.Element;
    }
  ) => React.ReactNode;
  placeholder?: string;
} & PropsWithClassNameOptional;
const HeaderSearchInput = ({
  className: searchInputclassName,
  placeholder = 'Start typing...',
  ...searchInputProps
}: Omit<SearchInputProps, 'onChange' | 'onReset'>) => {
  const {setSearchString} = useContext(FindAndApplyContext);
  return (
    <SearchInput
      className={classnames('o-find-and-apply__search', searchInputclassName)}
      aria-label='Search by keyword or phrase'
      placeholder={placeholder}
      onChange={debounce((e) => setSearchString(e.target.value), 500)}
      onReset={() => setSearchString('')}
      {...searchInputProps}
    />
  );
};

const Header = ({children, className, ...rest}: HeaderProps) => {
  const {setSearchString, ...context} = useContext(FindAndApplyContext);
  return (
    <SplitCard.Section
      border='none'
      className={classnames('o-find-and-apply__header', className)}
      {...rest}
    >
      {children ? (
        children!({HeaderSearchInput, setSearchString, ...context})
      ) : (
        <HeaderSearchInput />
      )}
    </SplitCard.Section>
  );
};

type BodyProps = OmitChildren<ContainerStyleProps> &
  RequireAtLeastOne<
    {
      children?: (context: State & {handleChange: (GenericModelType) => void}) => React.ReactNode;
      rowLabelRenderer?: (item: GenericModelType) => React.ReactNode;
      beforeSearchMessage?: string;
      noResultsMessage?: string;
    },
    'children' | 'rowLabelRenderer'
  >;
const Body = ({
  children,
  rowLabelRenderer,
  className,
  beforeSearchMessage,
  noResultsMessage,
  ...rest
}: BodyProps) => {
  const {
    searchResults,
    selectedItems,
    setSelectedItems,
    radio,
    searchString,
    query,
    fetchInitial,
    ...context
  } = useContext(FindAndApplyContext);

  const handleMultiSelect = useCallback(
    (currentItem: GenericModelType) => {
      setSelectedItems(
        selectedItems.has(currentItem)
          ? selectedItems.delete(currentItem)
          : selectedItems.add(currentItem)
      );
    },
    [selectedItems, setSelectedItems]
  );

  const handleChange = radio ? (item) => setSelectedItems(Set([item])) : handleMultiSelect;

  // without a higher order component provided, we will default to rendering rows of checkboxes ourselves
  if (!searchString && !fetchInitial) {
    return <Message>{beforeSearchMessage || 'No results found'}</Message>;
  }
  if (!query(searchString).isResourcePopulated() && query(searchString).isResourceValid()) {
    return <LoadingIndicator />;
  }
  if (searchString && query(searchString).isResourcePopulated() && searchResults.isEmpty()) {
    return <Message>{noResultsMessage || `No results found`}</Message>;
  }

  if (children) {
    return (
      <SplitCard.Section className={classnames('o-find-and-apply__body', className)} {...rest}>
        {children({
          searchResults,
          selectedItems,
          setSelectedItems,
          radio,
          searchString,
          query,
          handleChange,
          fetchInitial,
          ...context
        })}
      </SplitCard.Section>
    );
  }

  return (
    <SplitCard.Section className={classnames('o-find-and-apply__body', className)} {...rest}>
      <ListGroup as='ul' border='none' className='mt-x0 pt-x0'>
        {searchResults.map((searchResult) => {
          if (!searchResult) {
            return null;
          }
          return (
            <ListGroupItem key={searchResult.getId()} as='li' className='gap-x1 flex items-start'>
              {radio ? (
                <Radio
                  id={searchResult.getId()}
                  name={searchResult.getId()}
                  aria-hidden={false}
                  checked={selectedItems.has(searchResult)}
                  onChange={() => setSelectedItems(Set([searchResult]))}
                />
              ) : (
                <Checkbox
                  id={searchResult.getId()}
                  name={searchResult.getId()}
                  aria-hidden={false}
                  checked={selectedItems.has(searchResult)}
                  onChange={() => handleMultiSelect(searchResult)}
                />
              )}
              <Label className='ml-x1' htmlFor={searchResult.getId()}>
                {rowLabelRenderer!(searchResult)}
              </Label>
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </SplitCard.Section>
  );
};

type SelectedItemsProps = ContainerStyleProps & {
  chipColor?: ChipColor;
  chipIcon?: IconProp;
  innerText: (model: GenericModelType) => React.ReactNode;
};
const SelectedItems = ({
  className,
  innerText,
  chipColor = 'brand',
  chipIcon = 'times',
  ...rest
}: SelectedItemsProps) => {
  const {radio, selectedItems, setSelectedItems} = useContext(FindAndApplyContext);

  // Since radio inputs are designed not to be selectable, it does not make any sense to display the chip panel
  if (radio) {
    console.error(
      'SelectedItems panel is not available for FindAndApply when radio prop is set to true'
    );
    return null;
  }

  if (selectedItems.isEmpty()) {
    return null;
  }
  return (
    <SplitCard.Section className={className} {...rest}>
      <>
        {selectedItems
          .map((selectedItem) => {
            if (!selectedItem) {
              return null;
            }
            return (
              <CheckboxChip
                key={selectedItem.getId()}
                color={chipColor}
                icon={chipIcon}
                onChange={() => setSelectedItems(selectedItems.delete(selectedItem))}
              >
                {innerText(selectedItem)}
              </CheckboxChip>
            );
          })
          .toArray()}
      </>
    </SplitCard.Section>
  );
};

type FooterProps = OmitChildren<ContainerStyleProps> & {
  children?: (
    state: State & {
      ApplyButton: (
        props: OmitChildren<ButtonProps<'div'>> & PropsWithChildrenOptional
      ) => JSX.Element;
    } & {
      ClearButton: (props: ButtonProps<'div'>) => JSX.Element;
    }
  ) => React.ReactNode;
};

const ApplyButton = ({
  children: buttonChildren,
  ...buttonProps
}: OmitChildren<ButtonProps<'div'>> & PropsWithChildrenOptional) => {
  const {onApply, selectedItems, onDismiss} = useContext(FindAndApplyContext);
  return (
    <Button
      onClick={() => {
        onApply(selectedItems.toList());
        onDismiss?.();
      }}
      {...buttonProps}
    >
      {buttonChildren || 'Apply'}
    </Button>
  );
};

const ClearButton = ({children: buttonChildren, ...buttonProps}: ButtonProps<'div'>) => {
  const {onClear, setSelectedItems} = useContext(FindAndApplyContext);
  return (
    <Button
      onClick={() => {
        setSelectedItems(Set());
        onClear?.();
      }}
      {...buttonProps}
    >
      {buttonChildren || 'Clear'}
    </Button>
  );
};

const Footer = ({children, className, ...rest}: FooterProps) => {
  const {onApply, selectedItems, onDismiss, onClear, setSelectedItems, ...context} =
    useContext(FindAndApplyContext);

  return (
    <SplitCard.Section className={className} {...rest}>
      {children ? (
        children!({
          ApplyButton,
          ClearButton,
          setSelectedItems,
          onApply,
          onDismiss,
          selectedItems,
          ...context
        })
      ) : (
        <ButtonGroup align='right'>
          <ApplyButton>Apply</ApplyButton>
        </ButtonGroup>
      )}
    </SplitCard.Section>
  );
};

const Message = ({className, ...rest}: TextProps<'span'>) => (
  <SplitCard.Section className='o-find-and-apply__message text-center'>
    <Text
      as='span'
      className={classnames('o-find-and-apply__msg mx-auto pt-x5 pb-x5', className)}
      color='tertiary'
      italic
      size='l'
      {...rest}
    />
  </SplitCard.Section>
);

FindAndApply.Header = Header;
FindAndApply.Body = Body;
FindAndApply.Message = Message;
FindAndApply.Footer = Footer;
FindAndApply.SelectedItems = SelectedItems;
FindAndApply.displayName = 'FindAndApply';

export default FindAndApply;
